import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDealStore } from '../../lib/stores/dealStore';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { useClientStore } from '../../lib/stores/clientStore';
import { useQuoteTemplateStore } from '../../lib/stores/quoteTemplateStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { Deal, DealNote } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { useDataCache } from '../../contexts/DataCacheContext';

interface UseLoadDealResult {
  currentDeal: Deal | null;
  setCurrentDeal: React.Dispatch<React.SetStateAction<Deal | null>>;
  loading: boolean;
  error: string | null;
  dealNotes: DealNote[];
  setDealNotes: React.Dispatch<React.SetStateAction<DealNote[]>>;
  loadingNotes: boolean;
  loadDealNotes: () => Promise<DealNote[]>;
  refresh: () => Promise<Deal | null>;
}

export const useLoadDeal = (dealId: string | undefined): UseLoadDealResult => {
  
  // States
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null);
  const [dealNotes, setDealNotes] = useState<DealNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Cache
  const cache = useDataCache();

  // Stores
  const dealStore = useDealStore();
  const dealServiceStore = useDealServiceStore();
  const clientStore = useClientStore();
  const quoteTemplateStore = useQuoteTemplateStore();
  const settingsStore = useSettingsStore();

  // Destructure what we need from the stores
  const { fetchDealById, fetchDealNotes: storeFetchDealNotes, fetchDealActivities } = dealStore;
  const { fetchDealServices } = dealServiceStore;
  const { fetchClientById } = clientStore;
  const { fetchTemplates } = quoteTemplateStore;
  const { fetchSettings } = settingsStore;

  // Refs for tracking and preventing race conditions
  const isLoadingRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const mountCountRef = useRef(0);
  const loadingStabilityRef = useRef(false);
  const latestLoadRequestRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const currentDealRef = useRef<Deal | null>(null);
  
  // Initialize latestLoadRequestRef on mount
  useEffect(() => {
    latestLoadRequestRef.current = Date.now();
    return () => {
      console.log("[useLoadDeal] Cleaning up latestLoadRequestRef");
    };
  }, []);

  // Core function to load deal data - Optimized to avoid duplications
  const loadDealData = async (): Promise<Deal | null> => {
    console.log("[useLoadDeal] LOAD_START: Starting loadDealData function");
    if (!dealId) {
      console.log("[useLoadDeal] ERROR: No deal ID provided");
      return null;
    }

    // Create a unique ID for this load operation for debugging
    const loadId = Date.now();
    console.log(`[useLoadDeal] LOAD_ID: ${loadId} for deal ${dealId}`);

    // IMPORTANT: Implement double-check pattern to avoid race conditions and support concurrent loading
    // Only allow one load operation at a time with a timeout
    if (isLoadingRef.current) {
      const lastLoadTime = mountCountRef.current || 0;
      const elapsed = Date.now() - lastLoadTime;

      // If loading for more than 10 seconds, allow a new request
      if (elapsed < 10000) {
        console.log(`[useLoadDeal] Load ${loadId}: Already loading data (${elapsed}ms elapsed), skipping duplicate fetch`);
        return currentDeal;
      }

      console.log(`[useLoadDeal] Load ${loadId}: Existing load timed out after ${elapsed}ms, allowing new fetch`);
    }

    // Mark as loading and track start time
    isLoadingRef.current = true;
    mountCountRef.current = Date.now();

    // Setup cancellation - but don't check mounted state immediately to avoid flickering
    const controller = new AbortController();

    // Clear any existing safety timeout
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    // Set a safety timeout to prevent UI from getting stuck
    timeoutRef.current = window.setTimeout(() => {
      console.log(`[useLoadDeal] Load ${loadId}: SAFETY TIMEOUT after 10 seconds`);
      isLoadingRef.current = false;
      // Use try-catch to prevent errors if component unmounted
      try {
        setLoading(false);
      } catch (e) {
        console.log("[useLoadDeal] Caught error trying to update unmounted component");
      }
    }, 10000);

    try {
      // Always set loading state but with a safe wrapper
      try {
        setLoading(true);
        setError(null);
      } catch (e) {
        console.log("[useLoadDeal] Caught error setting initial loading state");
      }

      console.log(`[useLoadDeal] Load ${loadId}: Loading deal data for ID:`, dealId);

      console.log(`[useLoadDeal] FETCH_START: Fetching deal ${dealId} from API`);
      // Fetch the deal without activities to avoid potential loops - use await to properly catch errors
      const deal = await fetchDealById(dealId);

      // If the component unmounted during fetch, don't try to update anything
      if (!deal) {
        console.log(`[useLoadDeal] FETCH_ERROR: Load ${loadId}: No deal data returned`);
        // Check session: if expired/anon, 42501 errors cause null return silently
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          console.log(`[useLoadDeal] FETCH_ERROR: Session expired or missing`);
          if (isMountedRef.current) setError('SESSION_EXPIRED');
        }
        return null;
      }

      console.log(`[useLoadDeal] FETCH_SUCCESS: Deal data retrieved:`, {
        id: deal.id,
        title: deal.title,
        status: deal.status,
        client_id: deal.client_id,
        hasClient: !!deal.client
      });

      // Use a safe update function that doesn't throw if component unmounted
      const safeSetCurrentDeal = (deal: Deal) => {
        try {
          if (isMountedRef.current) {
            setCurrentDeal(prevDeal => {
              // Check for significant changes to avoid unnecessary re-renders
              if (prevDeal && prevDeal.id === deal.id &&
                  prevDeal.updated_at === deal.updated_at) {
                console.log("[useLoadDeal] Deal unchanged, skipping state update");
                return prevDeal;
              }
              return deal;
            });
          }
        } catch (e) {
          console.log("[useLoadDeal] Caught error updating deal state");
        }
      };

      console.log("[useLoadDeal] STATE_UPDATE: Setting currentDeal state with deal:", deal.title);
      safeSetCurrentDeal(deal);

      // Load secondary data in parallel for efficiency
      try {
        console.log("[useLoadDeal] Fetching secondary data in parallel");

        // Prepare all promises to fetch resources in parallel
        const promises = [];

        // Deal services - with cost recalculation
        const servicesPromise = fetchDealServices(dealId)
          .then(async (services) => {
            // After loading services, recalculate costs and margin
            if (services && services.length > 0) {
              try {
                const { recalculateDealAmount } = dealServiceStore;
                await recalculateDealAmount(dealId);
                console.log("[useLoadDeal] Recalculated deal costs and margin after loading services");
              } catch (err) {
                console.error("[useLoadDeal] Error recalculating deal amount:", err);
              }
            }
            return services;
          })
          .catch(e => {
            console.error("[useLoadDeal] Error fetching deal services:", e);
            return [];
          });
        promises.push(servicesPromise);

        // Notes and activities - unified in a single load to avoid duplication
        // Using loadDealNotes that already fetches both notes and activities
        const notesActivitiesPromise = loadDealNotes()
          .catch(e => {
            console.error("[useLoadDeal] Error loading notes and activities:", e);
            return [];
          });
        promises.push(notesActivitiesPromise);

        // Templates and settings
        const templatesPromise = fetchTemplates()
          .catch(e => {
            console.error("[useLoadDeal] Error fetching templates:", e);
            return [];
          });
        promises.push(templatesPromise);

        const settingsPromise = fetchSettings()
          .catch(e => {
            console.error("[useLoadDeal] Error fetching settings:", e);
            return null;
          });
        promises.push(settingsPromise);

        // Client data if needed
        if (deal.client_id && !deal.client) {
          const clientPromise = fetchClientById(deal.client_id)
            .catch(e => {
              console.error("[useLoadDeal] Error fetching client details:", e);
              return null;
            });
          promises.push(clientPromise);
        }

        // Wait for all promises to settle (not just resolve)
        await Promise.allSettled(promises);

        // We don't need to manually set activities because loadDealNotes already handles this
        // This eliminates the duplicate loading of activities that was happening before

        console.log("[useLoadDeal] Secondary data fetched successfully in parallel");
      } catch (secondaryErr) {
        // This catch should never be hit due to Promise.allSettled above
        console.error("[useLoadDeal] Unexpected error in secondary data loading:", secondaryErr);
      }

      console.log("[useLoadDeal] All data loaded successfully for deal:", dealId);
      return deal;
    } catch (err) {
      console.error(`[useLoadDeal] Load ${loadId}: Error loading deal:`, err);

      // Safe error state update
      try {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to load deal');
        }
      } catch (e) {
        console.log("[useLoadDeal] Caught error updating error state");
      }

      return null;
    } finally {
      // Clear the safety timeout
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Reset loading state - safely
      try {
        if (isMountedRef.current) {
          setLoading(false);
        }
      } catch (e) {
        console.log("[useLoadDeal] Caught error in finally block");
      }

      // Always reset loading ref state
      isLoadingRef.current = false;
      console.log(`[useLoadDeal] Load ${loadId}: Completed, loading state reset`);

      // Always cleanup resources
      controller.abort();
    }
  };

  // Function to load deal notes and activities with race condition protection
  // Using useCallback to memoize this function and prevent unnecessary re-renders
  // This is important since this function is passed to components and used in useEffect deps

  // Approccio completamente rivisto: caricare e impostare direttamente sia note che attività
  const loadDealNotes = React.useCallback(async (): Promise<DealNote[]> => {
    if (!dealId) {
      console.log("[useLoadDeal] loadDealNotes: No ID provided");
      return [];
    }

    // Generate unique request ID for tracking
    const loadRequestId = Date.now();
    console.log(`[useLoadDeal] loadDealNotes (${loadRequestId}): Starting fetch for deal ${dealId}`);

    // Set loading state ONLY if component is still mounted
    if (isMountedRef.current) {
      try {
        setLoadingNotes(true);
      } catch (e) {
        console.log("[useLoadDeal] Caught error setting loadingNotes state");
      }
    } else {
      return [];
    }

    // Self-resetting safety timer
    const safetyTimer = setTimeout(() => {
      if (isMountedRef.current) {
        console.log(`[useLoadDeal] loadDealNotes (${loadRequestId}): Safety timeout triggered`);
        try {
          setLoadingNotes(false);
        } catch (e) {
          console.log("[useLoadDeal] Caught error resetting loadingNotes state");
        }
      }
    }, 5000); // 5 second timeout

    try {
      console.log(`[useLoadDeal] loadDealNotes (${loadRequestId}): Fetching notes and activities in parallel`);

      // Initialize result variables
      let notes: DealNote[] = [];
      let activities: any[] = [];

      // Use Promise.allSettled to fetch both simultaneously, and continue even if one fails
      const [notesResult, activitiesResult] = await Promise.allSettled([
        storeFetchDealNotes(dealId),
        fetchDealActivities(dealId)
      ]);

      // Process notes result
      if (notesResult.status === 'fulfilled') {
        notes = notesResult.value || [];
        console.log(`[useLoadDeal] Successfully fetched ${notes.length} notes`);
      } else {
        console.error(`[useLoadDeal] Error fetching notes:`, notesResult.reason);
      }

      // Process activities result
      if (activitiesResult.status === 'fulfilled') {
        activities = activitiesResult.value || [];
        console.log(`[useLoadDeal] Successfully fetched ${activities.length} activities`);
      } else {
        console.error(`[useLoadDeal] Error fetching activities:`, activitiesResult.reason);
      }

      // Only proceed with state updates if component is still mounted
      if (!isMountedRef.current) {
        console.log(`[useLoadDeal] Component unmounted during loadDealNotes, aborting state updates`);
        return notes;
      }

      // APPROCCIO SEMPLIFICATO: Aggiorniamo ENTRAMBI gli stati direttamente
      try {
        // 1. Aggiorna le note
        setDealNotes(notes);
        
        // 2. Aggiorna le attività direttamente nel currentDeal, se è disponibile
        // Utilizziamo il ref invece dello stato per evitare il loop di rendering
        if (activities.length > 0 && currentDealRef.current) {
          console.log(`[useLoadDeal] Directly updating currentDeal with ${activities.length} activities`);
          
          setCurrentDeal(prev => {
            if (!prev) return null;
            return {
              ...prev,
              activities: activities // Sostituisci completamente le attività con quelle appena caricate
            };
          });
          
          // Salva una lista degli ID delle attività per il controllo successivo
          const activityIds = new Set(activities.map(a => a.id));
          
          // Aggiorniamo le reference di tracking
          (window as any)._lastLoadedActivityIds = activityIds;
        }
      } catch (updateErr) {
        console.error(`[useLoadDeal] Error updating state:`, updateErr);
      }

      console.log(`[useLoadDeal] loadDealNotes (${loadRequestId}): All operations completed`);
      return notes;
    } catch (error) {
      console.error(`[useLoadDeal] Unexpected error in loadDealNotes:`, error);
      return [];
    } finally {
      // Always cleanup resources
      clearTimeout(safetyTimer);

      // Always reset loading state if component is still mounted
      if (isMountedRef.current) {
        try {
          setLoadingNotes(false);
        } catch (e) {
          console.log(`[useLoadDeal] Error resetting loading state`);
        }
      }
    }
  // Non includiamo più currentDeal nelle dipendenze, utilizziamo il ref invece per evitare il loop
   
  }, [dealId, isMountedRef, setCurrentDeal, setDealNotes, storeFetchDealNotes, fetchDealActivities]);

  // Mount status is tracked via the isMountedRef defined earlier
  
  // Initialize deal data loading when dealId changes
  useEffect(() => {
    // Reset for each new invocation of the effect
    loadingStabilityRef.current = false;
    
    // Track this specific effect invocation with a unique ID
    const effectId = Date.now();
    
    // Use a stable flag to prevent useEffect re-execution during render
    if (loadingStabilityRef.current) {
      return;
    }
    
    // Set the flag to prevent re-execution
    loadingStabilityRef.current = true;
    
    // Mark component as mounted
    isMountedRef.current = true;
    
    // Create new abort controller for this effect
    const controller = new AbortController();
    const isActive = true;
    
    // Skip if no dealId
    if (!dealId) {
      setLoading(false); // Important: set loading to false even when skipping
      return;
    }
    
    setLoading(true); // Explicitly set loading to true at the start
    
    // Use Promise with safeguards to handle loading
    const loadWithSafety = async () => {
      try {
        if (!isMountedRef.current) {
          console.log(`[useLoadDeal] EFFECT_ABORTED: Component no longer mounted, aborting load`);
          return;
        }
        
        console.log(`[useLoadDeal] EFFECT_EXECUTE: Calling loadDealData for deal: ${dealId}`);
        // Load the deal data
        const result = await loadDealData();
        console.log(`[useLoadDeal] EFFECT_RESULT:`, result ? 'Deal loaded successfully' : 'No deal data returned');
        
        if (!isMountedRef.current) {
          console.log(`[useLoadDeal] EFFECT_UNMOUNTED: Component unmounted during data load`);
          return;
        }
        
        // Double check to make sure loading is set to false
        if (loading) {
          console.log(`[useLoadDeal] EFFECT_LOADING_RESET: Ensuring loading state is reset`);
          setLoading(false);
        }
        
        console.log(`[useLoadDeal] EFFECT_COMPLETE: Data load completed successfully`);
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error(`[useLoadDeal] EFFECT_ERROR: Error in loadDealData:`, err);
        
        // Only update error state if component is still mounted
        console.log(`[useLoadDeal] EFFECT_ERROR_STATE: Setting error state`);
        setError("Si è verificato un errore inaspettato nel caricamento del deal.");
        setLoading(false); // Ensure loading is set to false on error
      }
    };
    
    // Start loading process
    console.log(`[useLoadDeal] EFFECT_EXECUTE: Starting loadWithSafety`);
    loadWithSafety();
    
    // Cleanup function
    return () => {
      console.log(`[useLoadDeal] EFFECT_CLEANUP: Starting cleanup for effect ${effectId}`);
      isMountedRef.current = false;
      controller.abort();
      
      // Clean up any timeouts
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Reset all loading states
      isLoadingRef.current = false;
      loadingStabilityRef.current = false;
      
      console.log(`[useLoadDeal] EFFECT_CLEANUP_COMPLETE: Cleanup finished for effect ${effectId}`);
    };
  // Rimosso 'loading' dalle dipendenze per evitare loop infiniti
  }, [dealId]);

  // Reference per tenere traccia degli ID già visti
  const seenNoteIdsRef = useRef<Set<string>>(new Set());
  const seenActivityIdsRef = useRef<Set<string>>(new Set());
  
  // Aggiorna i set di ID quando cambiano i dati
  useEffect(() => {
    seenNoteIdsRef.current = new Set(dealNotes.map(note => note.id));
  }, [dealNotes]);
  
  // Aggiorna il ref di currentDeal quando cambia lo stato
  useEffect(() => {
    currentDealRef.current = currentDeal;
  }, [currentDeal]);
  
  useEffect(() => {
    if (currentDeal?.activities) {
      seenActivityIdsRef.current = new Set(currentDeal.activities.map(a => a.id));
    }
  }, [currentDeal?.activities]);
  
  // TEMPORARY DISABLED: Auto-refresh causing massive network calls
  // Auto-refresh periodico delle note ed attività
  useEffect(() => {
    if (!dealId || !isMountedRef.current) return;

    console.log("[useLoadDeal] Auto-refresh temporarily disabled to prevent network spam");
    return; // EARLY RETURN TO DISABLE AUTO-REFRESH

    // Funzione per controllare aggiornamenti in background
    const checkForUpdates = async () => {
      // Only check for updates if document is visible and component mounted
      if (document.visibilityState !== 'visible' || !isMountedRef.current) {
        return;
      }

      try {
        console.log("[useLoadDeal] Running background check for deal updates");

        // Notify that update check is starting
        document.dispatchEvent(new CustomEvent('dealCheckingUpdates', {
          detail: { checking: true, dealId }
        }));

        // Use Promise.allSettled to run all fetches in parallel and continue even if one fails
        const [notesResult, activitiesResult] = await Promise.allSettled([
          storeFetchDealNotes(dealId),
          fetchDealActivities(dealId, true) // Force refresh to bypass caching
        ]);

        // Skip processing if component was unmounted during fetch
        if (!isMountedRef.current) {
          return;
        }

        // Process notes results
        let newNotes: DealNote[] = [];
        if (notesResult.status === 'fulfilled' && notesResult.value) {
          const latestNotes = notesResult.value;
          
          // Recupera gli ID delle note già visualizzate
          const lastLoadedNoteIds = new Set(dealNotes.map(note => note.id));
          
          // Confronta con gli ID esistenti
          newNotes = latestNotes.filter(note => !lastLoadedNoteIds.has(note.id));

          // Se ci sono nuove note, aggiorna lo stato
          const hasNewNotes = newNotes.length > 0;
          
          if (hasNewNotes && isMountedRef.current) {
            console.log(`[useLoadDeal] Found ${newNotes.length} new notes during background check`);
            
            // Aggiorna lo stato delle note direttamente
            setDealNotes(latestNotes);
            
            // Dispatch un evento solo per le note nuove
            document.dispatchEvent(new CustomEvent('newNotesAvailable', {
              detail: {
                newNotes,
                dealId
              }
            }));
          }
        }

        // Process activities results
        let newActivities: any[] = [];
        if (activitiesResult.status === 'fulfilled' && activitiesResult.value) {
          const latestActivities = activitiesResult.value;
          
          // Recupera gli ID delle attività già caricate usando il ref
          const lastLoadedActivityIds = currentDealRef.current?.activities?.map(a => a.id) || [];
          const lastLoadedActivityIdSet = new Set(lastLoadedActivityIds);
          
          // Confronta con gli ID esistenti
          newActivities = latestActivities.filter(activity => !lastLoadedActivityIdSet.has(activity.id));
          
          // Se ci sono nuove attività, aggiorna lo stato
          const hasNewActivities = newActivities.length > 0;
          
          if (hasNewActivities && isMountedRef.current && currentDealRef.current) {
            console.log(`[useLoadDeal] Found ${newActivities.length} new activities during background check`);
            
            // Combina le attività esistenti con quelle nuove usando il ref
            const combinedActivities = [
              ...currentDealRef.current.activities || [],
              ...newActivities
            ];
            
            // Aggiorna lo stato direttamente
            setCurrentDeal(prev => {
              if (!prev) return null;
              return {
                ...prev,
                activities: combinedActivities
              };
            });
            
            // Dispatch un evento solo per le attività nuove
            document.dispatchEvent(new CustomEvent('newActivitiesAvailable', {
              detail: {
                newActivities,
                dealId
              }
            }));
          }
        }
        
        // Notifica generica solo se ci sono novità
        if ((newNotes.length > 0 || newActivities.length > 0) && isMountedRef.current) {
          // Usa toast di sistema o altra notifica visibile per informare l'utente
          document.dispatchEvent(new CustomEvent('contentUpdated', {
            detail: {
              hasNewNotes: newNotes.length > 0,
              hasNewActivities: newActivities.length > 0,
              dealId
            }
          }));
        }
      } catch (err) {
        console.error("[useLoadDeal] Error in background update check:", err);
      } finally {
        // Always notify that check is complete
        if (isMountedRef.current) {
          document.dispatchEvent(new CustomEvent('dealCheckingUpdates', {
            detail: { checking: false, dealId }
          }));
        }
      }
    };

    // Run check every 20 seconds (reduced from 30 for more responsive updates)
    const refreshInterval = setInterval(checkForUpdates, 20000);

    // Run immediately after initial load ma con un ritardo maggiore per evitare falsi allarmi
    // Aumentiamo il delay per dare più tempo all'UI di stabilizzarsi dopo l'aggiunta di task
    const initialCheckTimeout = setTimeout(checkForUpdates, 20000);

    return () => {
      console.log("[useLoadDeal] Cleaning up background check interval");
      clearInterval(refreshInterval);
      clearTimeout(initialCheckTimeout);
    };
  // Importante: rimuoviamo TUTTE le dipendenze che possono cambiare frequentemente
  // come dealNotes e currentDeal per prevenire cicli infiniti
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Function to manually refresh all deal data
  const refresh = async (): Promise<Deal | null> => {
    console.log("[useLoadDeal] Manual refresh requested");

    if (!dealId || !isMountedRef.current) {
      console.log("[useLoadDeal] Refresh aborted - no deal ID or component unmounted");
      return null;
    }

    try {
      // Notify UI that refresh is starting
      document.dispatchEvent(new CustomEvent('dealCheckingUpdates', {
        detail: { checking: true, dealId }
      }));

      // Load everything in parallel for maximum efficiency
      console.log("[useLoadDeal] Starting parallel data refresh");

      // Run all data loading operations in parallel
      const operations = [
        // First reload the deal data which contains most info.
        // force=true: bypassa la dedup di fetchDealById — se un'altra fetch
        // dello stesso deal è in volo (background check, anteprime) la dedup
        // restituirebbe il deal in formato LISTA → testata stale/incompleta.
        fetchDealById(dealId, true),

        // Load notes and activities with forced refresh
        storeFetchDealNotes(dealId),
        fetchDealActivities(dealId, true),

        // Also refresh services data with cost recalculation
        fetchDealServices(dealId).then(async (services) => {
          if (services && services.length > 0) {
            try {
              const { recalculateDealAmount } = dealServiceStore;
              await recalculateDealAmount(dealId);
              console.log("[useLoadDeal] Recalculated deal costs and margin during refresh");
            } catch (err) {
              console.error("[useLoadDeal] Error recalculating deal amount during refresh:", err);
            }
          }
          return services;
        })
      ];

      // Use Promise.allSettled to handle errors gracefully
      const results = await Promise.allSettled(operations);

      if (!isMountedRef.current) {
        console.log("[useLoadDeal] Component unmounted during refresh, aborting state updates");
        return null;
      }

      // Process deal data result
      let updatedDeal: Deal | null = null;
      if (results[0].status === 'fulfilled' && results[0].value) {
        updatedDeal = results[0].value;
        setCurrentDeal(updatedDeal);
      } else {
        console.error("[useLoadDeal] Error refreshing deal data:",
          results[0].status === 'rejected' ? results[0].reason : "No deal data returned");
      }

      // Process notes result
      if (results[1].status === 'fulfilled' && results[1].value) {
        setDealNotes(results[1].value);
      }

      // Process activities result - update currentDeal.activities explicitly
      if (results[2].status === 'fulfilled' && results[2].value) {
        const activities = results[2].value;
        setCurrentDeal(prev => {
          if (!prev) return null;
          return {
            ...prev,
            activities: activities
          };
        });
        console.log(`[useLoadDeal] Updated currentDeal with ${activities.length} activities after refresh`);
      }

      console.log("[useLoadDeal] Refresh completed successfully");
      return updatedDeal;
    } catch (error) {
      console.error("[useLoadDeal] Error during manual refresh:", error);
      return null;
    } finally {
      // Always notify that refresh is complete
      if (isMountedRef.current) {
        document.dispatchEvent(new CustomEvent('dealCheckingUpdates', {
          detail: { checking: false, dealId }
        }));
      }
    }
  };

  return {
    currentDeal,
    setCurrentDeal,
    loading,
    error,
    dealNotes,
    setDealNotes,
    loadingNotes,
    loadDealNotes,
    refresh
  };
};